import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { useVirtualizerLoadMore } from "@/modern/hooks/useVirtualizerLoadMore";
import { cn } from "@/modern/utils";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import type { FileUploadDayGroup } from "@/react/components/entityViews/files/utils/groupFilesByUploadDay";
import { FileDayGroupHeader } from "./FileDayGroupHeader";

const GRID_GAP_PX = 12;
/** Initial estimate for list rows — refined via measureElement. */
const LIST_ROW_ESTIMATE_PX = 76;
/** Day header height (sticky=false, mb-0). Keep close to rendered height. */
const DAY_HEADER_PX = 56;
/** Grid rows use fixed height so image decode does not thrash measureElement. */
const GRID_ROW_FALLBACK_PX = 172;

type GridRowItem = {
  type: "grid-row";
  key: string;
  files: SwivuFile[];
};

type ListRowItem = {
  type: "list-row";
  key: string;
  file: SwivuFile;
};

type DayHeaderItem = {
  type: "day-header";
  key: string;
  label: string;
  files: SwivuFile[];
};

type VirtualItem = GridRowItem | ListRowItem | DayHeaderItem;

function chunkFiles(files: SwivuFile[], cols: number): SwivuFile[][] {
  if (cols <= 0) return [];
  const rows: SwivuFile[][] = [];
  for (let i = 0; i < files.length; i += cols) {
    rows.push(files.slice(i, i + cols));
  }
  return rows;
}

function appendFileRows(
  items: VirtualItem[],
  files: SwivuFile[],
  {
    viewMode,
    cols,
    keyPrefix,
  }: {
    viewMode: "grid" | "list";
    cols: number;
    keyPrefix: string;
  },
) {
  if (viewMode === "grid") {
    chunkFiles(files, cols).forEach((rowFiles, rowIndex) => {
      items.push({
        type: "grid-row",
        key: `${keyPrefix}-row-${rowIndex}`,
        files: rowFiles,
      });
    });
    return;
  }
  for (const file of files) {
    items.push({
      type: "list-row",
      key: `${keyPrefix}-file-${file.id}`,
      file,
    });
  }
}

function buildVirtualItems({
  viewMode,
  cols,
  files,
  dayGroups,
}: {
  viewMode: "grid" | "list";
  cols: number;
  files: SwivuFile[];
  dayGroups: FileUploadDayGroup[] | null;
}): VirtualItem[] {
  const items: VirtualItem[] = [];

  if (dayGroups) {
    for (const group of dayGroups) {
      items.push({
        type: "day-header",
        key: `day-${group.dayKey}`,
        label: group.label,
        files: group.files,
      });
      appendFileRows(items, group.files, {
        viewMode,
        cols,
        keyPrefix: `day-${group.dayKey}`,
      });
    }
    return items;
  }

  appendFileRows(items, files, { viewMode, cols, keyPrefix: "flat" });
  return items;
}

function fixedHeightForItem(
  item: VirtualItem,
  gridRowHeight: number,
): number | undefined {
  if (item.type === "grid-row") return gridRowHeight;
  if (item.type === "day-header") return DAY_HEADER_PX;
  return undefined;
}

export interface FileBrowserVirtualBodyProps {
  viewMode: "grid" | "list";
  cols: number;
  files: SwivuFile[];
  dayGroups: FileUploadDayGroup[] | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  renderGridTile: (file: SwivuFile) => ReactNode;
  renderListRow: (file: SwivuFile) => ReactNode;
  enableMultiSelect: boolean;
  isSelectionBusy: boolean;
  getDaySelectionState: (dayFiles: SwivuFile[]) => boolean | "indeterminate";
  toggleDaySelection: (dayFiles: SwivuFile[]) => void;
}

export function FileBrowserVirtualBody({
  viewMode,
  cols,
  files,
  dayGroups,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  renderGridTile,
  renderListRow,
  enableMultiSelect,
  isSelectionBusy,
  getDaySelectionState,
  toggleDaySelection,
}: FileBrowserVirtualBodyProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth;
      setGridWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = useMemo(
    () => buildVirtualItems({ viewMode, cols, files, dayGroups }),
    [viewMode, cols, files, dayGroups],
  );

  const gridRowHeight = useMemo(() => {
    if (viewMode !== "grid" || cols <= 0 || gridWidth <= 0) return GRID_ROW_FALLBACK_PX;
    const cell = Math.floor((gridWidth - GRID_GAP_PX * (cols - 1)) / cols);
    return Math.max(cell, 48) + GRID_GAP_PX;
  }, [viewMode, cols, gridWidth]);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const gridRowHeightRef = useRef(gridRowHeight);
  gridRowHeightRef.current = gridRowHeight;

  const estimateSize = useCallback((index: number) => {
    const item = itemsRef.current[index];
    if (!item) return LIST_ROW_ESTIMATE_PX;
    if (item.type === "day-header") return DAY_HEADER_PX;
    if (item.type === "grid-row") return gridRowHeightRef.current;
    return LIST_ROW_ESTIMATE_PX;
  }, []);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: 5,
    getItemKey: (index) => items[index]?.key ?? index,
  });

  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, gridRowHeight, viewMode, cols]);

  useVirtualizerLoadMore(virtualizer, { hasNextPage, isFetchingNextPage, fetchNextPage });

  // Sticky overlay must follow the real scroll element; virtualizer.scrollOffset
  // alone lagged. Only commit React state when the active day changes.
  const [stickyDay, setStickyDay] = useState<DayHeaderItem | null>(null);
  const stickyDayKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const scrollRoot = scrollRef.current;
    if (!scrollRoot) return;

    const resolveStickyDay = (): DayHeaderItem | null => {
      if (!dayGroups || itemsRef.current.length === 0) return null;
      const scrollOffset = scrollRoot.scrollTop;
      const at = virtualizer.getVirtualItemForOffset(Math.max(0, scrollOffset));
      if (!at) return null;
      for (let i = at.index; i >= 0; i -= 1) {
        const item = itemsRef.current[i];
        if (item?.type === "day-header") {
          const start = virtualizer.getOffsetForIndex(i, "start")?.[0] ?? 0;
          if (start < scrollOffset - 1) return item;
          return null;
        }
      }
      return null;
    };

    const sync = () => {
      const next = resolveStickyDay();
      const nextKey = next?.key ?? null;
      if (nextKey === stickyDayKeyRef.current) return;
      stickyDayKeyRef.current = nextKey;
      setStickyDay(next);
    };

    sync();
    scrollRoot.addEventListener("scroll", sync, { passive: true });
    return () => scrollRoot.removeEventListener("scroll", sync);
  }, [dayGroups, virtualizer, items]);

  const virtualItems = virtualizer.getVirtualItems();

  const renderDayHeader = (item: DayHeaderItem) => (
    <FileDayGroupHeader
      label={item.label}
      count={item.files.length}
      sticky={false}
      className="mb-0"
      selectionChecked={enableMultiSelect ? getDaySelectionState(item.files) : undefined}
      onSelectionChange={enableMultiSelect ? () => toggleDaySelection(item.files) : undefined}
      selectionDisabled={isSelectionBusy}
    />
  );

  return (
    <div ref={scrollRef} className="relative min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
      {/* Always mounted (height 0) so sticky show/hide doesn’t shift scroll. */}
      <div className="sticky top-0 z-[2] h-0 overflow-visible">
        <div
          className={cn("bg-background", !stickyDay && "pointer-events-none invisible")}
          style={{ minHeight: stickyDay ? DAY_HEADER_PX : 0 }}
        >
          {stickyDay ? renderDayHeader(stickyDay) : null}
        </div>
      </div>

      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        <div
          className="absolute left-0 top-0 w-full"
          style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}
        >
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            if (!item) return null;

            const useFixedSize = item.type === "grid-row" || item.type === "day-header";
            const fixedHeight = fixedHeightForItem(item, gridRowHeight);

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={useFixedSize ? undefined : virtualizer.measureElement}
                className="min-w-0 w-full"
                style={fixedHeight != null ? { height: fixedHeight } : undefined}
              >
                {item.type === "day-header" ? (
                  <section
                    className="h-full"
                    aria-label={`${item.label}, ${item.files.length} files`}
                  >
                    {renderDayHeader(item)}
                  </section>
                ) : null}

                {item.type === "grid-row" ? (
                  <div
                    className="grid h-full min-w-0"
                    style={{
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      gap: GRID_GAP_PX,
                      paddingBottom: GRID_GAP_PX,
                    }}
                  >
                    {item.files.map((file) => renderGridTile(file))}
                  </div>
                ) : null}

                {item.type === "list-row" ? (
                  <div className="pb-2">{renderListRow(item.file)}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
